import { Star } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { GapCategory, GapReviewExecutionSummary } from '@momentum/types';

import { useFavoriteRuns } from '../hooks/useFavoriteRuns';
import { toggleFavoriteRun } from '../lib/favorite-runs';
import { pluralize, shortDate } from '../lib/format';
import {
  countRunsByVariant,
  filterRunOptions,
  RUN_VARIANT_FILTERS,
  RUN_VARIANT_LABELS,
  sortRunOptionsByFavorite,
  toRunOptions,
  type RunOption,
  type RunVariantFilter,
} from '../lib/run-picker';

interface RunPickerProps {
  executions: GapReviewExecutionSummary[];
  /** The selected run's id. Always a run — there is no "all runs" option. */
  value: string;
  onChange: (executionId: string) => void;
}

/**
 * The queue's run picker: which execution's gaps the queue loads.
 *
 * It picks rather than filters, and that is the whole shape of the surface: one
 * run's drafts are fetched at a time, so there is no "All drafts" entry to fall
 * back to. The gap counts on the rows come from the run summaries, which are
 * counted in the database — the picker can therefore say what each run holds
 * without any of it having been fetched.
 *
 * A combobox rather than a `<select>`, because a native option list cannot be
 * searched, cannot be filtered down to one pipeline, and cannot draw a run's id
 * on a second line without turning every row into an unreadable sentence. All
 * three matter here: the ids are the identifier a reviewer pastes into a script
 * or a Slack thread, and there are enough runs that finding one by scrolling is
 * the slow way round.
 *
 * The pipeline chips and the typed query are deliberately NOT in the URL, unlike
 * the queue's own filters. They narrow what the picker OFFERS, not what the
 * queue shows — a link carrying "delivery runs, typed 267" would restore a
 * half-open dropdown rather than a view of anything.
 *
 * Stars are in `localStorage` for the mirror-image reason: they outlive the page
 * but belong to one reviewer, so they are neither in the URL nor on the run.
 */
export function RunPicker({ executions, value, onChange }: RunPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [variant, setVariant] = useState<RunVariantFilter>('all');
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const { favoriteRunIds, toggleFavorite } = useFavoriteRuns();

  const options = useMemo(() => toRunOptions(executions), [executions]);
  const variantCounts = useMemo(() => countRunsByVariant(options), [options]);
  const selectedOption = options.find((option) => option.executionId === value);

  const entries = useMemo(
    () =>
      sortRunOptionsByFavorite(
        filterRunOptions(options, { variant, query }),
        favoriteRunIds
      ),
    [favoriteRunIds, options, query, variant]
  );

  // The highlight follows the list rather than the other way round: a query that
  // shortens the list must not leave it pointing past the end.
  useEffect(() => {
    setHighlightIndex((index) =>
      Math.min(index, Math.max(entries.length - 1, 0))
    );
  }, [entries.length]);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(pointerEvent: MouseEvent) {
      const target = pointerEvent.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setIsOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const open = () => {
    setIsOpen(true);
    setQuery('');
    // Opening lands on what is already selected, so ↓↵ moves one run rather than
    // jumping to the top of the list.
    const selectedIndex = entries.findIndex(
      (entry) => entry.executionId === value
    );
    setHighlightIndex(Math.max(selectedIndex, 0));
    // The input mounts with the popover, so the focus has to wait for it.
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  const close = ({ refocus }: { refocus: boolean }) => {
    setIsOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const select = (option: RunOption) => {
    onChange(option.executionId);
    close({ refocus: true });
  };

  /**
   * Starring a run moves it, so the highlight is moved with it rather than left
   * on whatever row slid into its place — otherwise the Enter that follows a
   * star would take a run the reviewer never pointed at. The next list has to be
   * worked out here because the memo above will not have run yet.
   */
  const toggleFavoriteOf = (option: RunOption) => {
    toggleFavorite(option.executionId);

    const nextEntries = sortRunOptionsByFavorite(
      filterRunOptions(options, { variant, query }),
      toggleFavoriteRun(favoriteRunIds, option.executionId)
    );
    setHighlightIndex(
      Math.max(
        nextEntries.findIndex(
          (entry) => entry.executionId === option.executionId
        ),
        0
      )
    );
  };

  const moveHighlight = (offset: number) => {
    if (entries.length === 0) return;
    setHighlightIndex(
      (index) => (index + offset + entries.length) % entries.length
    );
  };

  const handleSearchKeyDown = (
    keyboardEvent: React.KeyboardEvent<HTMLInputElement>
  ) => {
    switch (keyboardEvent.key) {
      case 'ArrowDown':
        keyboardEvent.preventDefault();
        moveHighlight(1);
        return;
      case 'ArrowUp':
        keyboardEvent.preventDefault();
        moveHighlight(-1);
        return;
      case 'Enter': {
        keyboardEvent.preventDefault();
        const highlighted = entries.at(highlightIndex);
        if (highlighted !== undefined) select(highlighted);
        return;
      }
      case 'Escape':
        keyboardEvent.preventDefault();
        close({ refocus: true });
        return;
      default:
        return;
    }
  };

  const activeEntryId =
    isOpen && entries.length > 0
      ? `${listboxId}-option-${highlightIndex}`
      : undefined;

  return (
    <div
      className="gr-runpick"
      ref={rootRef}
      // Tabbing out of the popover closes it; the reviewer has left the picker,
      // and a dropdown floating over a queue nobody is pointing at is litter.
      onBlur={(focusEvent) => {
        if (!focusEvent.currentTarget.contains(focusEvent.relatedTarget)) {
          setIsOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="gr-runpick-trigger"
        ref={triggerRef}
        aria-label="Run filter"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? close({ refocus: false }) : open())}
      >
        {/* Two lines, like the rows it picks from: the run id is nearly as wide
            as the queue itself, and beside the scope name it left neither
            legible. */}
        <span className="gr-runpick-triggerlabel">
          {selectedOption === undefined ? (
            // Only while the summaries and the selection disagree — a run named
            // in a link that no longer exists, say. Naming the id is more useful
            // than an empty trigger, because it says WHICH run is missing.
            <span className="gr-runpick-triggerline">{value}</span>
          ) : (
            <>
              <span className="gr-runpick-triggerline">
                <VariantTag variant={selectedOption.variant} />
                <span className="gr-runpick-ellipsis">
                  {selectedOption.scopeNames}
                </span>
              </span>
              <span className="gr-runpick-id">
                {selectedOption.executionId}
              </span>
            </>
          )}
        </span>
      </button>

      {isOpen && (
        <div className="gr-runpick-pop">
          <div
            className="gr-runpick-variants"
            role="group"
            aria-label="Pipeline"
          >
            {RUN_VARIANT_FILTERS.map((variantFilter) => (
              <button
                key={variantFilter}
                type="button"
                className="gr-runpick-variant"
                data-variant={variantFilter}
                aria-pressed={variant === variantFilter}
                onClick={() => setVariant(variantFilter)}
              >
                {RUN_VARIANT_LABELS[variantFilter]}{' '}
                {variantCounts[variantFilter]}
              </button>
            ))}
          </div>

          <input
            ref={searchRef}
            className="gr-runpick-search"
            type="text"
            role="combobox"
            aria-label="Filter runs"
            aria-controls={listboxId}
            aria-expanded
            aria-autocomplete="list"
            aria-activedescendant={activeEntryId}
            placeholder="Filter by id, scope or date…"
            value={query}
            onChange={(changeEvent) => setQuery(changeEvent.target.value)}
            onKeyDown={handleSearchKeyDown}
          />

          <ul className="gr-runpick-list" id={listboxId} role="listbox">
            {entries.length === 0 && (
              <li className="gr-runpick-empty">No run matches</li>
            )}
            {entries.map((option, position) => (
              <li
                key={option.executionId}
                className="gr-runpick-row"
                // Pointer and keyboard drive the same highlight, so a click
                // after a mouse-over never lands on a different row than the
                // one under the cursor. On the row rather than the option, so
                // reaching for the star does not drop the highlight behind it.
                onMouseEnter={() => setHighlightIndex(position)}
              >
                <button
                  type="button"
                  id={`${listboxId}-option-${position}`}
                  className="gr-runpick-option"
                  role="option"
                  aria-selected={option.executionId === value}
                  data-active={position === highlightIndex ? 'true' : undefined}
                  onClick={() => select(option)}
                >
                  <span className="gr-runpick-optionhead">
                    <VariantTag variant={option.variant} />
                    <span className="gr-runpick-scopes">
                      {option.scopeNames}
                    </span>
                    {option.isLatest && (
                      <i className="gr-runpick-latest">latest</i>
                    )}
                  </span>
                  {/* The id gets a line to itself: it is nearly as wide as the
                      queue pane, and sharing one with the date left a separator
                      dangling at the wrap. */}
                  <span className="gr-runpick-optionmeta">
                    <span className="gr-runpick-id">{option.executionId}</span>
                    <span>
                      {shortDate(option.completedAt)} ·{' '}
                      {pluralize(option.draftCount, 'gap')}
                    </span>
                  </span>
                </button>

                <FavoriteToggle
                  option={option}
                  isFavorite={favoriteRunIds.has(option.executionId)}
                  onToggle={() => toggleFavoriteOf(option)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface FavoriteToggleProps {
  option: RunOption;
  isFavorite: boolean;
  onToggle: () => void;
}

/**
 * The star that pins one run to the top of the list.
 *
 * A button of its own beside the row rather than a control inside it, because a
 * row that both selects a run and toggles a star from the same click target has
 * no way to tell the two apart — and the star is the one press here that must
 * NOT change which run the queue is showing.
 *
 * The label names the run rather than saying "favorite": read out of context,
 * a list of eleven identical "Favorite" buttons is a list of nothing.
 */
function FavoriteToggle({ option, isFavorite, onToggle }: FavoriteToggleProps) {
  return (
    <button
      type="button"
      className="gr-runpick-star"
      aria-pressed={isFavorite}
      aria-label={`${isFavorite ? 'Unfavorite' : 'Favorite'} run ${
        option.executionId
      }`}
      onClick={onToggle}
    >
      <Star size={13} aria-hidden />
    </button>
  );
}

/** Which pipeline produced the run, as a tag rather than as a word in the line. */
function VariantTag({ variant }: { variant: GapCategory }) {
  return (
    <i className="gr-runpick-tag" data-variant={variant}>
      {variant}
    </i>
  );
}
